import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api.js';
import { useBrand } from './BrandContext';

const TagSettingsContext = createContext();

const useTags = () => {
  const { activeBrand } = useBrand();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const [tagArticles, setTagArticles] = useState([]);
  const [articleLoading, setArticleLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [filteredTags, setFilteredTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);

  // Buscar todas as tags
  const fetchTags = useCallback(async () => {
    // Só fazer requisição se houver token
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      // Adicionar timestamp para evitar cache do navegador
      const timestamp = new Date().getTime();
      const res = await api.get(`/tags?_t=${timestamp}`);
      // Usar diretamente os dados retornados pelo backend que já incluem a contagem
      setTags(res.data.data);
      setError(null);
    } catch (err) {
      console.error('Erro ao buscar tags:', err);
      setError('Não foi possível carregar as tags');
    } finally {
      setLoading(false);
    }
  }, [activeBrand]);

  // Buscar artigos de uma tag específica
  const fetchTagArticles = useCallback(async (tagId) => {
    try {
      setArticleLoading(true);
      const res = await api.get(`/tags/${tagId}/articles`);
      setTagArticles(res.data.data || []);
      setError(null);
    } catch (err) {
      console.error('Erro ao buscar artigos da tag:', err);
      setError('Não foi possível carregar os artigos desta tag');
    } finally {
      setArticleLoading(false);
    }
  }, []);

  // Criar nova tag
  const createTag = async (tagData) => {
    try {
      setLoading(true);
      const res = await api.post('/tags', tagData);
      setTags(prev => [...prev, { ...res.data.data, articlesCount: 0 }]);
      return { success: true, data: res.data.data };
    } catch (err) {
      console.error('Erro ao criar tag:', err);
      setError(err.response?.data?.message || 'Erro ao criar tag');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Atualizar tag
  const updateTag = async (tagId, tagData) => {
    try {
      setLoading(true);
      const res = await api.put(`/tags/${tagId}`, tagData);
      
      // Buscar a contagem atualizada de artigos
      const articlesRes = await api.get(`/tags/${tagId}/articles`);
      const articlesCount = articlesRes.data.count || 0;
      
      setTags(prev => prev.map(tag => 
        tag._id === tagId ? { 
          ...tag, 
          ...res.data.data,
          articlesCount 
        } : tag
      ));
      
      if (selectedTag?._id === tagId) {
        setSelectedTag(prev => ({ 
          ...prev, 
          ...res.data.data,
          articlesCount 
        }));
      }
      
      return { success: true, data: res.data.data };
    } catch (err) {
      console.error('Erro ao atualizar tag:', err);
      setError(err.response?.data?.message || 'Erro ao atualizar tag');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Atualizar contagem de artigos para todas as tags
  // Otimizado: usa fetchTags que já retorna articlesCount do backend (1 req ao invés de N)
  const refreshTagCounts = useCallback(async () => {
    await fetchTags();
  }, [fetchTags]);

  // Deletar tag
  const deleteTag = async (tagId) => {
    // Validação de ID - aceitar IDs válidos do MongoDB (24 caracteres hexadecimais)
    if (!tagId || typeof tagId !== 'string' || !/^[0-9a-fA-F]{24}$/.test(tagId)) {
      console.error('ID de tag inválido para exclusão:', tagId);
      const error = new Error('ID de tag inválido');
      setError(error.message);
      throw error;
    }

    try {
      setLoading(true);
      await api.delete(`/tags/${tagId}`);
      setTags(prev => prev.filter(tag => tag._id !== tagId));
      setFilteredTags(prev => prev.filter(tag => tag._id !== tagId));
      if (selectedTag?._id === tagId) {
        setSelectedTag(null);
      }
      setSelectedTags(prev => prev.filter(id => id !== tagId));
      await fetchTags();
      return { success: true };
    } catch (err) {
      console.error('Erro ao deletar tag:', err);
      setError(err.response?.data?.message || 'Erro ao deletar tag');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Toggle selection mode
  const toggleSelectionMode = useCallback(() => {
    setSelectionMode(prev => !prev);
    if (selectionMode) {
      setSelectedTags([]);
    }
  }, [selectionMode]);

  // Toggle tag selection
  const toggleTagSelection = useCallback((tagId) => {
    setSelectedTags(prev => {
      if (prev.includes(tagId)) {
        return prev.filter(id => id !== tagId);
      }
      return [...prev, tagId];
    });
  }, []);

  // Efeito para filtrar e ordenar tags
  useEffect(() => {
    const filtered = tags.filter(tag =>
      tag.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'articles':
          return b.articlesCount - a.articlesCount;
        case 'recent':
          return new Date(b.createdAt) - new Date(a.createdAt);
        default:
          return 0;
      }
    });
    setFilteredTags(sorted);
  }, [tags, searchQuery, sortBy]);

  // Efeito para carregar tags iniciais e ao trocar de marca
  useEffect(() => {
    fetchTags();
    // Resetar seleção ao trocar de marca
    setSelectedTag(null);
    setSelectedTags([]);
    setSelectionMode(false);
  }, [fetchTags]);

  // Efeito para atualizar tags quando uma nova tag é criada em outra seção
  useEffect(() => {
    const handleTagsUpdated = () => {
      fetchTags();
    };
    window.addEventListener('tags-updated', handleTagsUpdated);
    return () => window.removeEventListener('tags-updated', handleTagsUpdated);
  }, [fetchTags]);

  // Efeito para carregar artigos quando uma tag é selecionada
  useEffect(() => {
    if (selectedTag) {
      fetchTagArticles(selectedTag._id);
    }
  }, [selectedTag, fetchTagArticles]);

  return {
    tags: filteredTags,
    loading,
    error,
    selectedTag,
    setSelectedTag,
    tagArticles,
    articleLoading,
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    selectedTags,
    setSelectedTags,
    selectionMode,
    createTag,
    updateTag,
    deleteTag,
    refreshTags: fetchTags,
    toggleSelectionMode,
    toggleTagSelection,
    refreshTagCounts
  };
};

export const TagSettingsProvider = ({ children }) => {
  const tagSettings = useTags();
  return (
    <TagSettingsContext.Provider value={tagSettings}>
      {children}
    </TagSettingsContext.Provider>
  );
};

export const useTagSettings = () => {
  const context = useContext(TagSettingsContext);
  if (!context) {
    throw new Error('useTagSettings must be used within a TagSettingsProvider');
  }
  return context;
}; 